import { create } from 'zustand';

/** Estado del cajón del asistente (ObsiaChatWidget), fuera del componente
 *  para que el atajo de teclado global de SantaColomaMapView lo alcance. */
/** Lo que el asistente está haciendo, tal como él mismo lo reporta. Los
 *  nombres son los del backend de OBSIA, no una traducción nuestra:
 *  `consultando` = una llamada a herramienta; `sintetizando` = desde la
 *  última herramienta hasta el primer token del modelo (el tramo largo, el
 *  que hoy se lee como colgado); `respondiendo` = llegando tokens.
 *  `idle` también es el estado antes de la primera pregunta. */
export type AssistantState =
  | 'idle'
  | 'consultando'
  | 'sintetizando'
  | 'respondiendo'
  | 'error';

export const ASSISTANT_STATES: readonly AssistantState[] = [
  'idle', 'consultando', 'sintetizando', 'respondiendo', 'error',
];

/** Estados en los que el asistente está trabajando y se espera que avance
 *  solo. Si se queda en uno de ellos, es que algo se rompió. */
export const ESTADOS_ACTIVOS: readonly AssistantState[] = [
  'consultando', 'sintetizando', 'respondiendo',
];

/** Un estado activo que no avanza en este tiempo se da por colgado.
 *  Medidas reales: 3-8 s las respuestas cortas, 53 s el máximo en
 *  producción y 75 s en local (una pregunta de histórico con 11 llamadas a
 *  herramienta). 120 s se quedaba corto para ese caso. Con 180 s el margen
 *  es real y sigue cazando un widget muerto: cada llamada a herramienta
 *  refresca el estado, así que un pipeline vivo nunca está 180 s callado. */
export const TIMEOUT_ACTIVIDAD_MS = 180_000;

interface AssistantPanelState {
  open: boolean;
  /** El iframe no se crea hasta la primera apertura; luego se queda. */
  mounted: boolean;
  /** Reportado por el widget vía postMessage. Se refleja en un atributo
   *  `data-assistant-state` del cajón: desde fuera del iframe (otro origen)
   *  es la única forma de saber si terminó sin sondear a ciegas. */
  activity: AssistantState;
  /** Etiqueta operacional que acompaña al estado ("Consultando alarmas
   *  activas"). La manda OBSIA; el host no la inventa ni la traduce. */
  activityDetail: string | null;
  toggle: () => void;
  close: () => void;
  setActivity: (a: AssistantState, detalle?: string | null) => void;
}

export const useAssistantPanel = create<AssistantPanelState>(set => ({
  open: false,
  mounted: false,
  activity: 'idle',
  activityDetail: null,
  toggle: () => set(s => ({ open: !s.open, mounted: s.mounted || !s.open })),
  close: () => set({ open: false }),
  setActivity: (activity, detalle = null) =>
    set({ activity, activityDetail: typeof detalle === 'string' ? detalle : null }),
}));

/** Interpreta un `postMessage` del widget. Devuelve null si no es del
 *  contrato — el host no se fía del contenido de un iframe ajeno, y un
 *  `state` desconocido o un `detalle` que no sea texto se descartan en vez
 *  de propagarse al store. Contrato en obsia-santacoloma-liaison.md. */
export type MensajeObsia =
  | { tipo: 'toggle' }
  | { tipo: 'state'; state: AssistantState; detalle: string | null }
  | { tipo: 'pan-to'; center: [number, number]; zoom: number | null }
  | { tipo: 'highlight'; assets: string[] };

/** Zoom al que el widget puede pedir que vuele la cámara. Fuera de este
 *  rango MapLibre no hace nada útil y un valor absurdo deja el mapa
 *  inservible para el operador. */
const ZOOM_MIN = 10;
const ZOOM_MAX = 20;

/** Tope de cruces resaltados a la vez: por encima el mapa se vuelve ruido
 *  y deja de señalar nada. */
const MAX_HIGHLIGHTS = 12;

function esLatitud(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= -90 && v <= 90;
}

function esLongitud(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= -180 && v <= 180;
}

export function interpretarMensajeObsia(data: unknown): MensajeObsia | null {
  if (typeof data !== 'object' || data === null) return null;
  const m = data as Record<string, unknown>;

  if (m.type === 'obsia:toggle-panel') return { tipo: 'toggle' };

  if (m.type === 'obsia:state') {
    if (typeof m.state !== 'string') return null;
    if (!ASSISTANT_STATES.includes(m.state as AssistantState)) return null;
    return {
      tipo: 'state',
      state: m.state as AssistantState,
      detalle: typeof m.detalle === 'string' && m.detalle.trim() ? m.detalle : null,
    };
  }

  // El widget manda {lat, lng}; MapLibre toma [lon, lat]. El orden se
  // invierte aquí, una sola vez, en vez de en cada punto de uso.
  if (m.type === 'obsia:pan-to') {
    if (!esLatitud(m.lat) || !esLongitud(m.lng)) return null;
    const zoom = typeof m.zoom === 'number' && m.zoom >= ZOOM_MIN && m.zoom <= ZOOM_MAX
      ? m.zoom
      : null;
    return { tipo: 'pan-to', center: [m.lng, m.lat], zoom };
  }

  if (m.type === 'obsia:highlight') {
    if (!Array.isArray(m.assets)) return null;
    const assets = m.assets
      .filter((a): a is string => typeof a === 'string')
      .map(a => a.trim().toUpperCase())
      .filter(a => /^SCG\d{3,4}$/.test(a))
      .slice(0, MAX_HIGHLIGHTS);
    return { tipo: 'highlight', assets };
  }

  return null;
}
